import React, { useEffect, useState, useMemo, useCallback } from "react";
import type {
  ActiveLayer,
  Entity,
  Event,
  ExplainResponse,
  Relationship,
  Source,
  Assessment,
  Weights,
} from "@nexus/contracts";
import { ACTIVE_LAYERS, V0_PERIOD } from "@nexus/contracts";
import type { IndexRankingEntry, TimelineEntry } from "./types";
import {
  fetchEntities,
  fetchEvents,
  fetchEventDetail,
  fetchRelationships,
  fetchIndexRanking,
  fetchExplain,
  simulateWeights,
  fetchTimeline,
} from "./api/client";
import { Header } from "./components/Header";
import { LeftSidebar } from "./components/LeftSidebar";
import { GlobeView } from "./components/GlobeView";
import { TechPowerIndex } from "./components/TechPowerIndex";
import { TimelineScrubber } from "./components/TimelineScrubber";
import { ExplainPanel } from "./components/ExplainPanel";
import { EventDetailModal } from "./components/EventDetailModal";
import { EntityDetailModal } from "./components/EntityDetailModal";

const DEFAULT_WEIGHTS: Weights = {
  AI: 0.25,
  CHIP: 0.25,
  ENERGY: 0.25,
  ROBOTICS: 0.25,
};

export const App: React.FC = () => {
  // 1. Core State
  const [activeLayers, setActiveLayers] = useState<Set<ActiveLayer>>(
    new Set(ACTIVE_LAYERS),
  );
  const [weights, setWeights] = useState<Weights>(DEFAULT_WEIGHTS);
  const [isSimulating, setIsSimulating] = useState<boolean>(false);
  const [timelineDate, setTimelineDate] = useState<string>("2026-06-30T23:59:59.000Z");

  // 2. Data State
  const [entities, setEntities] = useState<Entity[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [ranking, setRanking] = useState<IndexRankingEntry[]>([]);
  const [timelineEntries, setTimelineEntries] = useState<TimelineEntry[]>([]);
  const [isLoadingRanking, setIsLoadingRanking] = useState<boolean>(true);

  // 3. Selection & Modals State
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [eventDetailSources, setEventDetailSources] = useState<Source[]>([]);
  const [eventDetailAssessments, setEventDetailAssessments] = useState<Assessment[]>([]);

  const [explainEntityId, setExplainEntityId] = useState<string | null>(null);
  const [explainData, setExplainData] = useState<ExplainResponse | null>(null);
  const [isEntityDetailOpen, setIsEntityDetailOpen] = useState<boolean>(false);

  // 4. Initial Load
  useEffect(() => {
    async function initData() {
      try {
        const [entRes, evRes, relRes, rankRes, timeRes] = await Promise.all([
          fetchEntities({ limit: 100 }),
          fetchEvents({ limit: 100 }),
          fetchRelationships({ limit: 100, asOf: timelineDate }),
          fetchIndexRanking({ period: V0_PERIOD }),
          fetchTimeline(),
        ]);

        setEntities(entRes.entities);
        setEvents(evRes.events);
        setRelationships(relRes.relationships);
        setRanking(rankRes.ranking);
        setTimelineEntries(timeRes.entries);
      } catch (err) {
        console.error("Initial data load error:", err);
      } finally {
        setIsLoadingRanking(false);
      }
    }

    initData();
  }, []);

  // Refetch time-sensitive data through the backend's date filters.
  useEffect(() => {
    async function updateAsOfData() {
      try {
        const [evRes, relRes] = await Promise.all([
          fetchEvents({ limit: 100, to: timelineDate }),
          fetchRelationships({ limit: 100, asOf: timelineDate }),
        ]);
        setEvents(evRes.events);
        setRelationships(relRes.relationships);
      } catch (err) {
        console.error("Failed to update data for asOf:", err);
      }
    }

    updateAsOfData();
  }, [timelineDate]);

  // Handle Layer Toggle
  const handleToggleLayer = useCallback((layer: ActiveLayer) => {
    setActiveLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layer)) {
        if (next.size > 1) next.delete(layer); // Keep at least one
      } else {
        next.add(layer);
      }
      return next;
    });
  }, []);

  const handleSelectAllLayers = useCallback(() => {
    setActiveLayers(new Set(ACTIVE_LAYERS));
  }, []);

  const handleClearAllLayers = useCallback(() => {
    setActiveLayers(new Set(["AI"]));
  }, []);

  // Handle Weight Slider Change -> Calls POST /api/index/simulate
  const handleChangeWeights = useCallback(
    async (newWeights: Weights) => {
      setWeights(newWeights);
      setIsSimulating(true);
      try {
        const res = await simulateWeights(newWeights, V0_PERIOD);
        setRanking(res.ranking);
      } catch (err) {
        console.error("Weight simulation failed:", err);
      } finally {
        setIsSimulating(false);
      }
    },
    [],
  );

  const handleResetWeights = useCallback(async () => {
    setWeights(DEFAULT_WEIGHTS);
    setIsSimulating(true);
    try {
      const rankRes = await fetchIndexRanking({ period: V0_PERIOD });
      setRanking(rankRes.ranking);
    } catch (err) {
      console.error("Reset ranking failed:", err);
    } finally {
      setIsSimulating(false);
    }
  }, []);

  // Open Explain Panel
  const handleOpenExplain = useCallback(async (entityId: string) => {
    setExplainEntityId(entityId);
    try {
      const data = await fetchExplain(entityId, { period: V0_PERIOD });
      setExplainData(data);
    } catch (err) {
      console.error("Failed to fetch explanation:", err);
    }
  }, []);

  // Select Event -> Fetch event details (sources & assessments)
  const handleSelectEvent = useCallback(async (ev: Event) => {
    setSelectedEvent(ev);
    try {
      const detail = await fetchEventDetail(ev.id);
      setEventDetailSources(detail.sources);
      setEventDetailAssessments(detail.assessments);
    } catch (err) {
      console.error("Failed to fetch event detail:", err);
    }
  }, []);

  const handleSelectEventId = useCallback(
    (eventId: string) => {
      const ev = events.find((e) => e.id === eventId);
      if (ev) {
        handleSelectEvent(ev);
      }
    },
    [events, handleSelectEvent],
  );

  // Compute Layer Event Counts
  const layerCounts = useMemo(() => {
    const counts: Record<ActiveLayer, number> = {
      AI: 0,
      CHIP: 0,
      ENERGY: 0,
      ROBOTICS: 0,
      CYBER: 0,
      HYBRID_WARFARE: 0,
    };
    for (const ev of events) {
      for (const l of ev.layers) {
        if (counts[l as ActiveLayer] !== undefined) {
          counts[l as ActiveLayer]++;
        }
      }
    }
    return counts;
  }, [events]);

  const selectedEntity = useMemo(() => {
    return entities.find((e) => e.id === selectedEntityId) || null;
  }, [entities, selectedEntityId]);

  const explainEntity = useMemo(() => {
    return entities.find((e) => e.id === explainEntityId) || null;
  }, [entities, explainEntityId]);

  return (
    <div className="command-center">
      {/* 1. Header with brand and mandatory MOCK DATA badge */}
      <Header
        entityCount={entities.length}
        eventCount={events.length}
        activeLayerCount={activeLayers.size}
        asOfPeriod={V0_PERIOD}
      />

      {/* 2. Main 3-Column Stage */}
      <main className="main-stage">
        {/* Left: Layers & Weights */}
        <LeftSidebar
          activeLayers={activeLayers}
          onToggleLayer={handleToggleLayer}
          onSelectAllLayers={handleSelectAllLayers}
          onClearAllLayers={handleClearAllLayers}
          layerCounts={layerCounts}
          weights={weights}
          onChangeWeights={handleChangeWeights}
          onResetWeights={handleResetWeights}
          isSimulating={isSimulating}
          entities={entities}
          events={events}
          selectedEntityId={selectedEntityId}
          onSelectEntity={(id) => {
            setSelectedEntityId(id);
            setIsEntityDetailOpen(true);
          }}
          onSelectEvent={handleSelectEvent}
        />

        {/* Center: 3D Globe with globe.gl */}
        <GlobeView
          entities={entities}
          events={events}
          relationships={relationships}
          activeLayers={activeLayers}
          selectedEntityId={selectedEntityId}
          selectedEventId={selectedEvent?.id || null}
          onSelectEntity={(id) => {
            setSelectedEntityId(id);
            setIsEntityDetailOpen(true);
          }}
          onSelectEvent={handleSelectEvent}
        />

        {/* Right: Tech Power Index */}
        <TechPowerIndex
          ranking={ranking}
          selectedEntityId={selectedEntityId}
          onSelectEntity={(id) => setSelectedEntityId(id)}
          onOpenExplain={handleOpenExplain}
          isLoading={isLoadingRanking}
        />
      </main>

      {/* 3. Timeline Scrubber (2023 -> 2026-H1) */}
      <TimelineScrubber
        entries={timelineEntries}
        currentDate={timelineDate}
        onChangeDate={setTimelineDate}
        activeLayers={activeLayers}
        onSelectEventId={handleSelectEventId}
      />

      {/* 4. Explain Panel Modal */}
      {explainEntityId && explainData && (
        <ExplainPanel
          data={explainData}
          onClose={() => {
            setExplainEntityId(null);
            setExplainData(null);
          }}
          entityNameKo={explainEntity?.nameKo}
          entityNameEn={explainEntity?.nameEn}
        />
      )}

      {/* 5. Event Detail Modal */}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          sources={eventDetailSources}
          assessments={eventDetailAssessments}
          relationships={relationships}
          entities={entities}
          onClose={() => setSelectedEvent(null)}
          onSelectEntity={(id) => {
            setSelectedEvent(null);
            setSelectedEntityId(id);
            setIsEntityDetailOpen(true);
          }}
        />
      )}

      {/* 6. Entity Detail Modal */}
      {isEntityDetailOpen && selectedEntity && (
        <EntityDetailModal
          entity={selectedEntity}
          relationships={relationships}
          events={events}
          entities={entities}
          onClose={() => setIsEntityDetailOpen(false)}
          onOpenExplain={(id) => {
            setIsEntityDetailOpen(false);
            handleOpenExplain(id);
          }}
          onSelectEvent={(ev) => {
            setIsEntityDetailOpen(false);
            handleSelectEvent(ev);
          }}
        />
      )}
    </div>
  );
};
